export const LANGUAGES = ['en', 'pt', 'ro', 'sp', 'de', 'fr', 'hr'] as const;
export type Language = typeof LANGUAGES[number];
export interface LessonIdentity {
  year: number;
  quarter: number;
  lessonNum: number;
}
export const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);

export function getSavedLanguage(): Language | null {
  const saved = localStorage.getItem('defaultLanguage');
  return isLanguage(saved) ? saved : null;
}

export function isLessonIdentity(value: unknown): value is LessonIdentity {
  const data = value as LessonIdentity | null;
  return !!data && Number.isInteger(data.year) && data.year >= 1 && data.year <= 9999 &&
    Number.isInteger(data.quarter) && data.quarter >= 1 && data.quarter <= 4 &&
    Number.isInteger(data.lessonNum) && data.lessonNum >= 1 && data.lessonNum <= 14;
}

export function parseCalendarDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-?(\d{2})-?(\d{2})$/.exec(value);
  if (!match || (value.length !== 8 && value.length !== 10)) return null;
  const [, y, m, d] = match;
  const date = new Date(0);
  date.setFullYear(Number(y), Number(m) - 1, Number(d));
  date.setHours(0, 0, 0, 0);
  return date.getFullYear() === Number(y) && date.getMonth() === Number(m) - 1 &&
    date.getDate() === Number(d) ? date : null;
}

export function isDateInfoFor(value: unknown, year: number, month: number, day: number): boolean {
  const data = value as { date?: unknown; weekday?: unknown; isOffline?: unknown } | null;
  const date = parseCalendarDate(data?.date);
  return isLessonIdentity(value) && !!date && data?.isOffline !== true &&
    date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day &&
    data?.weekday === date.getDay();
}

export type LessonRoute =
  | { kind: 'lesson'; language: Language; identity: LessonIdentity }
  | { kind: 'current'; language?: Language }
  | { kind: 'invalid' };

export function parseLessonRoute(pathname: string): LessonRoute {
  const path = pathname.replace(/\/$/, '');
  if (path === '' || path === '/ssl') return { kind: 'current' };
  const parts = path.split('/');
  if (parts[1] !== 'ssl' || !isLanguage(parts[2])) return { kind: 'invalid' };
  if (parts.length === 3) return { kind: 'current', language: parts[2] };
  if (parts.length !== 6 || !parts.slice(3).every(value => /^\d+$/.test(value))) {
    return { kind: 'invalid' };
  }
  const identity = { year: Number(parts[3]), quarter: Number(parts[4]), lessonNum: Number(parts[5]) };
  return isLessonIdentity(identity) ? { kind: 'lesson', language: parts[2], identity } : { kind: 'invalid' };
}

export function lessonUrl(language: Language, identity: LessonIdentity, hash = ''): string {
  return `/ssl/${language}/${identity.year}/${identity.quarter}/${identity.lessonNum}${hash}`;
}

export function lessonIdentityKey(identity: LessonIdentity): string {
  return `${identity.year}-${identity.quarter}-${identity.lessonNum}`;
}

export function noteKey(language: Language, identity: LessonIdentity, day: number | 'heading', question?: number): string {
  return `notes-${language}-${identity.year}-${identity.quarter}-${identity.lessonNum}-${day}${question === undefined ? '' : `-${question}`}`;
}
